import React from "react";

export interface SeatingChartGeometrySeat<TSeat> {
  key: React.Key;
  column: number;
  value: TSeat;
}

export interface SeatingChartGeometryRow<TRow, TSeat> {
  key: React.Key;
  value: TRow;
  seats: SeatingChartGeometrySeat<TSeat>[];
}

interface SeatingChartGeometrySeatContext<TRow, TSeat> {
  row: SeatingChartGeometryRow<TRow, TSeat>;
  rowIndex: number;
  seat: SeatingChartGeometrySeat<TSeat>;
}

interface SeatingChartGeometryRailContext<TRow, TSeat> {
  row: SeatingChartGeometryRow<TRow, TSeat>;
  rowIndex: number;
  side: "left" | "right";
}

interface SeatingChartGeometryProps<TRow, TSeat> {
  rows: SeatingChartGeometryRow<TRow, TSeat>[];
  columnCount: number;
  seatWidth: number | string;
  rowRailWidth: number | string;
  columnGap: number | string;
  rowGap: number | string;
  className?: string;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  containerDataAttributes?: Record<string, string>;
  rowClassName?: string;
  getRowProps?: (
    row: SeatingChartGeometryRow<TRow, TSeat>,
    rowIndex: number
  ) => React.HTMLAttributes<HTMLDivElement>;
  getRowDataAttributes?: (
    row: SeatingChartGeometryRow<TRow, TSeat>,
    rowIndex: number
  ) => Record<string, string>;
  minHeight?: number | string;
  renderSeat: (
    context: SeatingChartGeometrySeatContext<TRow, TSeat>
  ) => React.ReactNode;
  renderRowRail: (
    context: SeatingChartGeometryRailContext<TRow, TSeat>
  ) => React.ReactNode;
  front?: React.ReactNode;
}

const cssLength = (value: number | string) =>
  typeof value === "number" ? `${value}px` : value;

/**
 * Neutral seating-chart layout primitive. Consumers own seat interaction and
 * presentation while this renderer owns the ordered grid, rail placement, and
 * front slot.
 */
export const renderSeatingChartGeometry = <TRow, TSeat>({
  rows,
  columnCount,
  seatWidth,
  rowRailWidth,
  columnGap,
  rowGap,
  className,
  containerProps,
  containerDataAttributes,
  rowClassName,
  getRowProps,
  getRowDataAttributes,
  minHeight,
  renderSeat,
  renderRowRail,
  front,
}: SeatingChartGeometryProps<TRow, TSeat>) => {
  const h = React.createElement;

  return h(
    "div",
    {
      ...containerProps,
      ...containerDataAttributes,
      "data-seating-chart-geometry": "true",
      className: className || containerProps?.className,
      style: {
        ...containerProps?.style,
        ...(minHeight === undefined ? {} : { minHeight }),
      },
    },
    h(
      "div",
      { style: { display: "grid", rowGap: cssLength(rowGap) } },
      rows.map((row, rowIndex) => {
        const rowProps = getRowProps?.(row, rowIndex);
        const rowDataAttributes = getRowDataAttributes?.(row, rowIndex);

        return h(
          "div",
          {
            ...rowProps,
            ...rowDataAttributes,
            key: row.key,
            "data-seating-chart-row": "true",
            className: rowClassName,
            style: {
              display: "grid",
              alignItems: "center",
              columnGap: cssLength(columnGap),
              gridTemplateColumns: `${cssLength(
                rowRailWidth
              )} repeat(${columnCount}, ${cssLength(seatWidth)}) ${cssLength(
                rowRailWidth
              )}`,
            },
          },
          h(
            "div",
            { style: { gridColumn: 1 } },
            renderRowRail({ row, rowIndex, side: "left" })
          ),
          ...row.seats.map((seat) =>
            h(
              "div",
              { key: seat.key, style: { gridColumn: seat.column } },
              renderSeat({ row, rowIndex, seat })
            )
          ),
          h(
            "div",
            { style: { gridColumn: columnCount + 2 } },
            renderRowRail({ row, rowIndex, side: "right" })
          )
        );
      })
    ),
    front
  );
};
